import React, { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { pad2 } from "./bookingFormat";
import PagePreview from "./PagePreview";
import MeetingTypes from "./MeetingTypes";
import PhotoCropper, { readImageFile } from "./PhotoCropper";

/*
 * TAB 2: PAGE. What leads see. Every field autosaves ("Changes go live as you
 * type"), and each save sends ONLY the field that changed: he tunes this while
 * other work is going on, and a save must not undo a field it never mentioned.
 */

const KEYS = [
  ["plan", "Plan", "crm-bkchip--plan"],
  ["fix", "Fix", "crm-bkchip--fix"],
  ["fit", "Fit", "crm-bkchip--fit"],
];

/** Business days from today, skipping the weekdays his window closes. */
function lastBookableDay(businessDays, window) {
  const d = new Date();
  let left = businessDays;
  for (let guard = 0; guard < 60 && left > 0; guard++) {
    d.setDate(d.getDate() + 1);
    if (window && window[d.getDay()]) left -= 1;
  }
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function swap(list, a, b) {
  const next = [...list];
  [next[a], next[b]] = [next[b], next[a]];
  return next;
}

function videoHost(url) {
  const u = String(url || "");
  if (/^https:\/\/(www\.|m\.)?(youtube\.com|youtu\.be)\//.test(u)) return "YouTube";
  if (/^https:\/\/(www\.)?loom\.com\//.test(u)) return "Loom";
  if (/^https:\/\/(www\.|player\.)?vimeo\.com\//.test(u)) return "Vimeo";
  return null;
}

function useAutosave(onSaved) {
  const [state, setState] = useState("idle");
  // Bumped after each save, so the live preview reloads with the new wording.
  const [version, setVersion] = useState(0);
  const timers = useRef({});
  // One timer per thing being edited, so a quick edit to the Fix card cannot
  // swallow a pending edit to the Plan card.
  const save = (field, value, delay = 700, timer = field) => {
    clearTimeout(timers.current[timer]);
    setState("pending");
    timers.current[timer] = setTimeout(async () => {
      setState("saving");
      try {
        onSaved(await api("/booking/settings", { method: "PUT", body: JSON.stringify({ [field]: value }) }));
        setState("saved");
        setVersion((v) => v + 1);
      } catch (err) {
        setState(`error:${err.message}`);
      }
    }, delay);
  };
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);
  return [state, save, version];
}

function AgendaCard({ chip, chipCls, agenda, isDefault, onChange, onRestore }) {
  const items = agenda.items;
  const setItems = (next) => onChange({ ...agenda, items: next });
  const move = (i, by) => {
    const next = [...items];
    const [item] = next.splice(i, 1);
    next.splice(i + by, 0, item);
    setItems(next);
  };
  return (
    <div className="crm-card crm-bkagenda">
      <div className="crm-bkagenda__head">
        <span className={`crm-bkchip ${chipCls}`}>{chip}</span>
        <button type="button" className="crm-linkact" disabled={isDefault} onClick={onRestore}>
          Restore default
        </button>
      </div>
      <label className="crm-bkmono">Label</label>
      <input
        className="crm-input crm-bkagenda__label"
        value={agenda.label}
        maxLength={60}
        onChange={(e) => onChange({ ...agenda, label: e.target.value })}
      />
      <label className="crm-bkmono">Lines · minute and title</label>
      {items.map((item, i) => (
        <div key={i} className="crm-bkline">
          <div className="crm-bkline__row">
            <input
              className={`crm-input crm-bkline__at${item.at >= 30 ? " is-bad" : ""}`}
              inputMode="numeric"
              value={pad2(item.at)}
              aria-label="Minute"
              onChange={(e) => {
                const at = Math.min(59, Math.max(0, parseInt(e.target.value.replace(/\D/g, "") || "0", 10)));
                setItems(items.map((x, j) => (j === i ? { ...x, at } : x)));
              }}
            />
            <input
              className="crm-input crm-bkline__title"
              value={item.title}
              maxLength={120}
              aria-label="Title"
              onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
            />
            <button type="button" className="crm-bkicon" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">
              ↑
            </button>
            <button
              type="button"
              className="crm-bkicon"
              disabled={i === items.length - 1}
              onClick={() => move(i, 1)}
              aria-label="Move down"
            >
              ↓
            </button>
            <button
              type="button"
              className="crm-bkicon"
              disabled={items.length === 1}
              onClick={() => setItems(items.filter((_, j) => j !== i))}
              aria-label="Remove line"
            >
              ×
            </button>
          </div>
          {item.at >= 30 ? (
            <p className="crm-hint crm-hint--warn">Starts at {item.at}. The call ends at 30, so this line never happens.</p>
          ) : null}
        </div>
      ))}
      {items.length < 8 ? (
        <button
          type="button"
          className="crm-linkact"
          onClick={() => setItems([...items, { at: Math.min(29, (items[items.length - 1]?.at ?? 0) + 5), title: "New line" }])}
        >
          + Add line
        </button>
      ) : null}
    </div>
  );
}

export default function PageTab({ data, onSaved }) {
  const { settings, defaults, calendar } = data;
  const [state, save, version] = useAutosave(onSaved);
  const [showPreview, setShowPreview] = useState(true);
  const [lookahead, setLookahead] = useState(settings.lookahead_business_days);
  const [title, setTitle] = useState(settings.title);
  const [agendas, setAgendas] = useState(settings.agendas);
  const [reads, setReads] = useState(settings.before_we_talk || []);
  const [hostTitle, setHostTitle] = useState(settings.host_title || "");
  const [hostPhoto, setHostPhoto] = useState(settings.host_photo || null);
  const [photoError, setPhotoError] = useState(null);
  // The photo being cropped: a picked file, or the current one being adjusted.
  const [cropping, setCropping] = useState(null);

  /*
   * The server gives a new page its id on its first save. Adopt it, so later
   * saves send the id and a retitled page keeps its address.
   */
  useEffect(() => {
    const saved = settings.before_we_talk || [];
    setReads((local) =>
      local.map((r, i) => (!r.id && saved[i] && saved[i].title === r.title ? { ...r, id: saved[i].id } : r))
    );
  }, [settings.before_we_talk]);
  const titleRef = useRef(null);

  const example = String(title || "")
    .replace(/\{\{\s*name\s*\}\}/g, "Jane Doe")
    .replace(/\{\{\s*company\s*\}\}/g, "Acme");

  const insert = (token) => {
    const el = titleRef.current;
    const at = el ? el.selectionStart : title.length;
    const next = `${title.slice(0, at)}${token}${title.slice(at)}`;
    setTitle(next);
    save("title", next);
  };

  const changeAgenda = (key, next) => {
    setAgendas({ ...agendas, [key]: next });
    // Only send a line list the server will accept: every title filled in.
    if (next.label.trim() && next.items.every((i) => i.title.trim())) {
      save("agendas", { [key]: { label: next.label, items: next.items } }, 700, `agendas.${key}`);
    }
  };

  const isDefault = (key) =>
    agendas[key].label === defaults.agendas[key].label &&
    JSON.stringify(agendas[key].items) === JSON.stringify(defaults.agendas[key].items);

  // A page saves once it has a title and a body, and a video link (if any) is https.
  const saveReads = (next) => {
    setReads(next);
    const complete = next.every(
      (r) => r.title.trim() && (r.body || "").trim() && (!r.video_url || /^https:\/\//.test(r.video_url.trim()))
    );
    if (complete) {
      save(
        "before_we_talk",
        next.map((r) => ({
          ...(r.id ? { id: r.id } : {}),
          title: r.title,
          teaser: r.teaser || "",
          video_url: (r.video_url || "").trim(),
          video_minutes: r.video_minutes || null,
          body: r.body,
        }))
      );
    }
  };

  const status = state.startsWith("error:") ? (
    <span className="crm-hint crm-hint--warn">{state.slice(6)}</span>
  ) : state === "saved" ? (
    <span className="crm-hint">✓ Saved</span>
  ) : state === "idle" ? null : (
    <span className="crm-hint">Saving…</span>
  );

  return (
    <div className={`crm-bkpagewrap${showPreview ? " has-preview" : ""}`}>
    <div className="crm-bkpage">
      <div className="crm-bkpage__intro">
        <p>What leads see on the booking and success screens. Changes go live as you type.</p>
        <span className="crm-bkpage__introright">
          {status}
          <button type="button" className="crm-btn crm-btn--secondary" onClick={() => setShowPreview((v) => !v)}>
            {showPreview ? "Hide preview" : "Show preview"}
          </button>
        </span>
      </div>

      <div className="crm-bkpage__grid">
        <section className="crm-card crm-bkbox">
          <h3>How far ahead</h3>
          <p className="crm-hint">Business days leads can book into, starting today.</p>
          <div className="crm-bkstepper">
            <button
              type="button"
              className="crm-bkicon crm-bkicon--box"
              disabled={lookahead <= 1}
              onClick={() => {
                setLookahead(lookahead - 1);
                save("lookahead_business_days", lookahead - 1, 400);
              }}
            >
              −
            </button>
            <b>{lookahead}</b>
            <button
              type="button"
              className="crm-bkicon crm-bkicon--box"
              disabled={lookahead >= 30}
              onClick={() => {
                setLookahead(lookahead + 1);
                save("lookahead_business_days", lookahead + 1, 400);
              }}
            >
              +
            </button>
            <span>business days</span>
          </div>
          <p className="crm-hint">
            Today, a lead can book up to <b>{lastBookableDay(lookahead, calendar.bookable_window)}</b>. Default is 10.
          </p>
        </section>

        <section className="crm-card crm-bkbox">
          <h3>Invite title</h3>
          <p className="crm-hint">The event name on their calendar and yours.</p>
          <input
            ref={titleRef}
            className="crm-input crm-bktitle"
            value={title}
            maxLength={120}
            onChange={(e) => {
              setTitle(e.target.value);
              if (e.target.value.trim()) save("title", e.target.value);
            }}
          />
          <div className="crm-bkinsert">
            <span className="crm-hint">Insert</span>
            <button type="button" onClick={() => insert("{{name}}")}>{"{{name}}"}</button>
            <button type="button" onClick={() => insert("{{company}}")}>{"{{company}}"}</button>
          </div>
          <div className="crm-bkexample">
            <span className="crm-hint">Jane Doe at Acme would see</span>
            <b>{example}</b>
          </div>
        </section>
      </div>

      <section className="crm-card crm-bkbox crm-bkhostbox">
        <h3>You're meeting</h3>
        <p className="crm-hint">The side column on the desktop booking page: your photo and one line about you.</p>
        <div className="crm-bkhostbox__row">
          {hostPhoto ? (
            <img className="crm-bkhostbox__photo" src={hostPhoto} alt="" />
          ) : (
            <span className="crm-bkhostbox__photo crm-bkhostbox__photo--initial">G</span>
          )}
          <div className="crm-bkhostbox__fields">
            <input
              className="crm-input"
              value={hostTitle}
              maxLength={140}
              placeholder="Founder, Remotion. We only do LinkedIn Ads for B2B."
              onChange={(e) => {
                setHostTitle(e.target.value);
                save("host_title", e.target.value);
              }}
            />
            <span className="crm-bkhostbox__actions">
              <label className="crm-btn crm-btn--secondary">
                {hostPhoto ? "Change photo" : "Upload photo"}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    e.target.value = "";
                    if (!file) return;
                    setPhotoError(null);
                    try {
                      setCropping(await readImageFile(file));
                    } catch (err) {
                      setPhotoError(err.message);
                    }
                  }}
                />
              </label>
              {hostPhoto ? (
                <>
                  <button type="button" className="crm-linkact" onClick={() => setCropping(hostPhoto)}>
                    Adjust crop
                  </button>
                  <button
                    type="button"
                    className="crm-linkact"
                    onClick={() => {
                      setHostPhoto(null);
                      save("host_photo", null, 0);
                    }}
                  >
                    Remove
                  </button>
                </>
              ) : null}
            </span>
            {photoError ? <p className="crm-hint crm-hint--warn">{photoError}</p> : null}
            {cropping ? (
              <PhotoCropper
                source={cropping}
                onCancel={() => setCropping(null)}
                onDone={(data) => {
                  setCropping(null);
                  setHostPhoto(data);
                  save("host_photo", data, 0);
                }}
              />
            ) : null}
          </div>
        </div>
      </section>

      <MeetingTypes
        types={settings.types || []}
        introMinutes={calendar.meeting_minutes}
        baseUrl={data.public_url ? data.public_url.replace(/\/book$/, "") : null}
        save={(field, value) => save(field, value, 700, "types")}
        travelSource={data.readiness && data.readiness.maps ? "google" : "estimate"}
      />

      <h3 className="crm-bkpage__h">What's this call for?</h3>
      <p className="crm-hint">The three choices on the success screen. Every agenda is 30 minutes.</p>
      <div className="crm-bkagendas">
        {KEYS.map(([key, chip, cls]) => (
          <AgendaCard
            key={key}
            chip={chip}
            chipCls={cls}
            agenda={agendas[key]}
            isDefault={isDefault(key)}
            onChange={(next) => changeAgenda(key, next)}
            onRestore={() => {
              setAgendas({ ...agendas, [key]: { key, ...defaults.agendas[key] } });
              save("agendas", { [key]: null }, 0, `agendas.${key}`);
            }}
          />
        ))}
      </div>

      <section className="crm-card crm-bkbox crm-bkreads">
        <div className="crm-bkreads__head">
          <h3>Before we talk</h3>
          <span className="crm-hint">{reads.length} of 4</span>
        </div>
        <p className="crm-hint">
          Short pages on the lead's success screen. They open inside the booking app, with a way back to the booking.
          Leave it empty and the section doesn't show at all.
        </p>
        {reads.map((r, i) => (
          <div key={r.id || `new-${i}`} className="crm-bkreadcard">
            <div className="crm-bkreadcard__head">
              <input
                className="crm-input crm-bkreadcard__title"
                placeholder="Title, e.g. What a Thought Leader Ad looks like"
                value={r.title}
                maxLength={120}
                onChange={(e) => saveReads(reads.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
              />
              <button type="button" className="crm-bkicon" disabled={i === 0} onClick={() => saveReads(swap(reads, i, i - 1))} aria-label="Move up">
                ↑
              </button>
              <button
                type="button"
                className="crm-bkicon"
                disabled={i === reads.length - 1}
                onClick={() => saveReads(swap(reads, i, i + 1))}
                aria-label="Move down"
              >
                ↓
              </button>
              <button type="button" className="crm-bkicon" onClick={() => saveReads(reads.filter((_, j) => j !== i))} aria-label="Remove page">
                ×
              </button>
            </div>
            <input
              className="crm-input"
              placeholder="One line under the title on the success screen (optional)"
              value={r.teaser || ""}
              maxLength={160}
              onChange={(e) => saveReads(reads.map((x, j) => (j === i ? { ...x, teaser: e.target.value } : x)))}
            />
            <input
              className="crm-input"
              placeholder="Video: a YouTube, Loom or Vimeo link (optional)"
              value={r.video_url || ""}
              onChange={(e) => saveReads(reads.map((x, j) => (j === i ? { ...x, video_url: e.target.value } : x)))}
            />
            {r.video_url && videoHost(r.video_url) ? (
              <label className="crm-bkreadcard__minutes">
                <span className="crm-hint">Length</span>
                <input
                  className="crm-input crm-input--narrow"
                  inputMode="numeric"
                  placeholder="2"
                  value={r.video_minutes || ""}
                  onChange={(e) => {
                    const n = parseInt(e.target.value.replace(/\D/g, ""), 10);
                    saveReads(reads.map((x, j) => (j === i ? { ...x, video_minutes: n > 0 ? Math.min(n, 90) : null } : x)));
                  }}
                />
                <span className="crm-hint">min, shown as "VIDEO · 2 MIN"</span>
              </label>
            ) : null}
            {r.video_url ? (
              <p className={`crm-hint${videoHost(r.video_url) ? "" : " crm-hint--warn"}`}>
                {videoHost(r.video_url) ? `✓ ${videoHost(r.video_url)} video, shown on the page` : "Only YouTube, Loom and Vimeo links play on the page."}
              </p>
            ) : null}
            <textarea
              className="crm-input crm-bkreadcard__body"
              rows={8}
              maxLength={8000}
              placeholder={"What you'd tell them before the call.\n\n## A heading\n- a bullet\n**bold**, and [a link](https://remotion.io)"}
              value={r.body || ""}
              onChange={(e) => saveReads(reads.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)))}
            />
            <p className="crm-hint">
              Blank line between paragraphs · <code>## </code> heading · <code>- </code> bullet · <code>**bold**</code> ·{" "}
              <code>[text](https://…)</code>
            </p>
          </div>
        ))}
        {reads.length < 4 ? (
          <button
            type="button"
            className="crm-linkact"
            onClick={() => setReads([...reads, { title: "", teaser: "", video_url: "", body: "" }])}
          >
            + Add page
          </button>
        ) : null}
      </section>
    </div>
    {showPreview ? <PagePreview version={version} types={settings.types || []} /> : null}
    </div>
  );
}
